import { useMemo, useState } from 'react'
import { Users, User, X } from 'lucide-react'

export interface Principal { type: 'user' | 'department'; id: string; name: string }

interface Props {
  users: { id: string; name: string }[]
  departments: { id: string; name: string }[]
  selected: Principal[]
  onAdd: (p: Principal) => void
  onRemove: (p: Principal) => void
}

export function PrincipalPicker({ users, departments, selected, onAdd, onRemove }: Props) {
  const [q, setQ] = useState('')

  const selectedIds = useMemo(
    () => new Set(selected.map((p) => `${p.type}:${p.id}`)),
    [selected]
  )

  const term = q.trim().toLowerCase()

  const filteredDepts = useMemo(
    () => departments.filter(
      (d) => !selectedIds.has(`department:${d.id}`) && (!term || d.name.toLowerCase().includes(term))
    ),
    [departments, selectedIds, term]
  )

  const filteredUsers = useMemo(
    () => users.filter(
      (u) => !selectedIds.has(`user:${u.id}`) && (!term || u.name.toLowerCase().includes(term))
    ),
    [users, selectedIds, term]
  )

  return (
    <div className="flex flex-col gap-2">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <span
              key={`${p.type}:${p.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-xs font-medium"
            >
              {p.type === 'department' ? <Users size={11} /> : <User size={11} />}
              {p.name}
              <button
                aria-label={`Remove ${p.name}`}
                onClick={() => onRemove(p)}
                className="ml-0.5 text-primary hover:text-foreground transition duration-150"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search people or departments…"
        className="w-full px-3 py-1.5 rounded-lg bg-muted text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 ring-border"
      />

      {/* List */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {filteredDepts.length > 0 && (
          <>
            <p className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50">
              Departments
            </p>
            {filteredDepts.map((d) => (
              <button
                key={d.id}
                onClick={() => onAdd({ type: 'department', id: d.id, name: d.name })}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition duration-150 text-sm"
              >
                <Users size={14} className="text-muted-foreground shrink-0" />
                <span className="text-foreground">{d.name}</span>
              </button>
            ))}
          </>
        )}

        {filteredUsers.length > 0 && (
          <>
            <p className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50">
              People
            </p>
            {filteredUsers.map((u) => (
              <button
                key={u.id}
                onClick={() => onAdd({ type: 'user', id: u.id, name: u.name })}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition duration-150 text-sm"
              >
                <User size={14} className="text-muted-foreground shrink-0" />
                <span className="text-foreground">{u.name}</span>
              </button>
            ))}
          </>
        )}

        {filteredDepts.length === 0 && filteredUsers.length === 0 && (
          <p className="px-3 py-3 text-sm text-muted-foreground text-center">
            {term ? 'No matches' : 'All entries selected'}
          </p>
        )}
      </div>
    </div>
  )
}
